import { useMemo } from 'react'

import type { DashboardLabTestEntry } from '~/api/graphql/generated/types'
import { useLabTestWidgetSubscription } from '~/api/graphql/subscriptions/labTests'

import { useSubscriptionRetry } from './useSubscriptionRetry'

/**
 * Custom hook for subscribing to lab test data changes for a specific patient
 * Automatically handles subscription retries for network errors and returns
 * data in DashboardLabTestEntry format
 *
 * @param patientId - The ID of the patient to subscribe to lab test updates for
 * @param offset - Pagination offset for lab tests list
 * @param limit - Maximum number of lab tests to fetch per page
 * @returns Object containing categorized lab tests, counters, loading state, and error state
 */
export const useLabTestsSubscription = (
  patientId: string,
  offset: number = 0,
  limit: number = 5,
): {
  completedLabTests: DashboardLabTestEntry[]
  completedLabTestsCount: number
  error: any
  inProgressLabTests: DashboardLabTestEntry[]
  inProgressLabTestsCount: number
  loading: boolean
  resultsReceivedLabTests: DashboardLabTestEntry[]
  resultsReceivedLabTestsCount: number
} => {
  const { data, loading, error, restart } = useLabTestWidgetSubscription(
    patientId,
    offset,
    limit,
  )

  const transformedData = useMemo(() => {
    if (!data?.labTestWidget) {
      return {
        completedLabTests: [],
        inProgressLabTests: [],
        resultsReceivedLabTests: [],
        completedLabTestsCount: 0,
        inProgressLabTestsCount: 0,
        resultsReceivedLabTestsCount: 0,
      }
    }

    const widget = data.labTestWidget
    const counters = widget.labTestCounters

    return {
      completedLabTests: widget.completedLabTests || [],
      inProgressLabTests: widget.inProgressLabTests || [],
      resultsReceivedLabTests: widget.resultsReceivedLabTests || [],
      completedLabTestsCount: counters?.completedLabTests ?? 0,
      inProgressLabTestsCount: counters?.inProgressLabTests ?? 0,
      resultsReceivedLabTestsCount: counters?.resultsReceivedLabTests ?? 0,
    }
  }, [data])

  useSubscriptionRetry({
    error,
    restart,
    subscriptionName: 'Lab Tests subscription',
  })

  return {
    completedLabTests: transformedData.completedLabTests,
    completedLabTestsCount: transformedData.completedLabTestsCount,
    inProgressLabTests: transformedData.inProgressLabTests,
    inProgressLabTestsCount: transformedData.inProgressLabTestsCount,
    resultsReceivedLabTests: transformedData.resultsReceivedLabTests,
    resultsReceivedLabTestsCount: transformedData.resultsReceivedLabTestsCount,
    loading,
    error,
  }
}
